import {
  Box,
  Button,
  Form,
  FormField,
  Heading,
  Layer,
  List,
  Text,
  TextInput
} from 'grommet'
import React, { useState } from 'react'
import { Constants, I18n } from 'galarm-config'
import { Add, Close, Edit, Trash } from 'grommet-icons'
import {
  arrayRemove,
  collection,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
  writeBatch
} from 'firebase/firestore'
import GlobalConfig from '../GlobalConfig'
import ErrorProvider from './ErrorProvider'
import { v4 as uuidv4 } from 'uuid'
import FormFieldWithNoBorder from './FormFieldWithNoBorder'
import { useSelector } from 'react-redux'
import { SecondaryText } from 'web-components'

const LABEL_COLORS = [
  '#DC2626', // Red
  '#059669', // Green
  '#2563EB', // Blue
  '#7C3AED', // Purple
  '#DB2777', // Pink
  '#EA580C', // Orange
  '#0891B2', // Cyan
  '#4338CA', // Indigo
  '#15803D', // Dark Green
  '#BE123C', // Dark Rose
  '#6366F1', // Indigo Blue
  '#0E7490' // Dark Cyan
]

const LabelItem = ({ label, onEdit, onDelete, isLast }) => {
  return (
    <Box
      direction="row"
      justify="between"
      align="center"
      pad="small"
      background="white"
      border={isLast ? false : { color: 'border', side: 'bottom' }}>
      <Box direction="row" align="center" gap="small">
        <Box
          width="20px"
          height="20px"
          round="small"
          background={label.color}
        />
        <Text>{label.name}</Text>
      </Box>
      <Box direction="row" gap="small">
        <Button
          icon={<Edit size="18" color="textColor" />}
          onClick={() => onEdit(label)}
        />
        <Button
          icon={<Trash size="18" color="textColor" />}
          onClick={() => onDelete(label)}
        />
      </Box>
    </Box>
  )
}

const LabelManager = ({ onClose }) => {
  const enterpriseAccountId = window.localStorage.getItem('enterpriseAccountId')
  const existingLabels = useSelector(state => state.accountInfo.labels)

  const [labels, setLabels] = useState(existingLabels)
  const [editingLabel, setEditingLabel] = useState(null)
  const [showEditLabelForm, setShowEditLabelForm] = useState(false)
  const [formValue, setFormValue] = useState({
    name: '',
    color: LABEL_COLORS[0]
  })
  const [errors, setErrors] = useState([])

  const addError = error => setErrors(errors.concat([error]))
  const onClearError = errorToClear => {
    const newErrors = errors.filter(error => error !== errorToClear)
    setErrors(newErrors)
  }

  const displayEditLabelForm = () => {
    setShowEditLabelForm(true)
  }

  const hideEditLabelForm = () => {
    setShowEditLabelForm(false)
    setEditingLabel(null)
    setFormValue({ name: '', color: LABEL_COLORS[0] })
  }

  const onSubmit = async () => {
    try {
      if (!formValue.name.trim()) {
        addError(I18n.t('labelNameRequired'))
        return
      }

      // Check for duplicate label names (case-insensitive)
      const normalizedName = formValue.name.trim().toLowerCase()
      const isDuplicate = labels.some(
        label =>
          label.name.toLowerCase() === normalizedName &&
          (!editingLabel || label.id !== editingLabel.id)
      )

      if (isDuplicate) {
        addError(I18n.t('labelAlreadyExists'))
        return
      }

      let updatedLabels
      if (editingLabel) {
        // Update existing label
        updatedLabels = labels.map(label =>
          label.id === editingLabel.id ? { ...label, ...formValue } : label
        )
      } else {
        // Add new label
        const newLabel = {
          id: uuidv4(),
          name: formValue.name,
          color: formValue.color
        }
        updatedLabels = [...labels, newLabel]
      }

      setLabels(updatedLabels)

      // Save to Firestore
      await updateDoc(
        doc(
          GlobalConfig.firestoreDb,
          'enterpriseAccounts',
          enterpriseAccountId
        ),
        { labels: updatedLabels }
      )

      // Reset form
      hideEditLabelForm()
    } catch (error) {
      console.error(error)
      addError(error.message)
    }
  }

  const onEdit = label => {
    setEditingLabel(label)
    setFormValue({ name: label.name, color: label.color })
    displayEditLabelForm()
  }

  const onDelete = async label => {
    try {
      GlobalConfig.showProgress({
        state: Constants.ProgressStates.IN_PROGRESS,
        message: I18n.t('deletingLabel'),
        closeable: false
      })

      const updatedLabels = labels.filter(l => l.id !== label.id)
      setLabels(updatedLabels)

      // Update the labels in enterprise account
      await updateDoc(
        doc(
          GlobalConfig.firestoreDb,
          'enterpriseAccounts',
          enterpriseAccountId
        ),
        { labels: updatedLabels }
      )

      // Get all alerts and remove the label from them
      const alertsQuery = query(
        collection(
          GlobalConfig.firestoreDb,
          'enterpriseAccounts',
          enterpriseAccountId,
          'alerts'
        )
      )
      const alertsSnapshot = await getDocs(alertsQuery)

      if (!alertsSnapshot.empty) {
        const batch = writeBatch(GlobalConfig.firestoreDb)

        alertsSnapshot.forEach(alertDoc => {
          const alert = alertDoc.data()
          if (alert.labels && alert.labels.includes(label.id)) {
            const updatedAlertLabels = alert.labels.filter(
              labelId => labelId !== label.id
            )
            batch.update(alertDoc.ref, { labels: updatedAlertLabels })
          }
        })

        await batch.commit()
      }

      // Also remove the label from any coordinator relying on it to resolve listeners,
      // otherwise their assignedLabels keeps a dangling id that never matches an alert again
      const coordinatorsQuery = query(
        collection(
          GlobalConfig.firestoreDb,
          'enterpriseAccounts',
          enterpriseAccountId,
          'members'
        ),
        where(
          'role',
          '==',
          Constants.ENTERPRISE_USER_ROLES.INCIDENT_COORDINATOR
        ),
        where('assignedLabels', 'array-contains', label.id)
      )
      const coordinatorsSnapshot = await getDocs(coordinatorsQuery)
      await Promise.all(
        coordinatorsSnapshot.docs.map(coordinatorDoc =>
          updateDoc(coordinatorDoc.ref, {
            assignedLabels: arrayRemove(label.id)
          })
        )
      )

      GlobalConfig.hideProgress()
    } catch (error) {
      console.error(error)
      addError(error.message)
      GlobalConfig.hideProgress()
    }
  }

  return (
    <ErrorProvider
      errors={errors}
      getErrorMessage={error => error}
      onClearError={onClearError}>
      <Box width="large" gap="small">
        <Box direction="row" justify="between" align="center">
          <Heading level={3} margin="small">
            {I18n.t('manageLabels')}
          </Heading>
          <Button icon={<Close />} onClick={onClose} />
        </Box>

        <Box>
          <Box
            background="white"
            round="small"
            overflow="auto"
            style={{ maxHeight: '300px' }}>
            {labels.length === 0 ? (
              <Text margin="small" color="textSecondary">
                {I18n.t('noLabelsYet')}
              </Text>
            ) : (
              <List border={false} pad="none" margin="none" data={labels}>
                {(label, index) => (
                  <LabelItem
                    label={label}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    isLast={index === labels.length - 1}
                  />
                )}
              </List>
            )}
          </Box>
        </Box>

        <Box align="start" margin={{ top: 'small' }}>
          <Button
            primary
            icon={<Add color="white" />}
            label={I18n.t('label')}
            style={{ color: 'white' }}
            onClick={displayEditLabelForm}
            alignSelf="start"
          />
        </Box>
      </Box>

      {showEditLabelForm && (
        <Layer
          onEsc={hideEditLabelForm}
          onClickOutside={hideEditLabelForm}
          position="center">
          <Box width="medium" pad="medium" gap="small">
            <Box direction="row" justify="between" align="center">
              <Heading level={3} margin="none">
                {editingLabel ? I18n.t('editLabel') : I18n.t('addLabel')}
              </Heading>
              <Button icon={<Close />} onClick={hideEditLabelForm} />
            </Box>

            <Form
              value={formValue}
              onChange={nextValue => setFormValue(nextValue)}
              onSubmit={onSubmit}>
              <FormField
                name="name"
                label={
                  <SecondaryText weight={500}>
                    {I18n.t('labelName')}
                  </SecondaryText>
                }
                required>
                <TextInput name="name" placeholder={I18n.t('enterLabelName')} />
              </FormField>

              <FormFieldWithNoBorder
                name="color"
                label={
                  <SecondaryText weight={500}>{I18n.t('color')}</SecondaryText>
                }>
                <Box
                  direction="row"
                  gap={{ row: 'medium', column: 'small' }}
                  wrap>
                  {LABEL_COLORS.map(color => (
                    <Box
                      key={color}
                      width="40px"
                      height="40px"
                      round="small"
                      background={color}
                      border={
                        formValue.color === color
                          ? { color: 'dark-1', size: '3px' }
                          : { color: 'border', size: '1px' }
                      }
                      onClick={() => setFormValue({ ...formValue, color })}
                      style={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </FormFieldWithNoBorder>

              <Box
                direction="row"
                gap="small"
                justify="end"
                margin={{ top: 'small' }}>
                <Button label={I18n.t('cancel')} onClick={hideEditLabelForm} />
                <Button
                  type="submit"
                  primary
                  label={editingLabel ? I18n.t('update') : I18n.t('add')}
                  style={{ color: 'white' }}
                />
              </Box>
            </Form>
          </Box>
        </Layer>
      )}
    </ErrorProvider>
  )
}

export default LabelManager
